import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { getErrorMessage } from '../../docs/utils/docsUtils';
import { useDeleteTrackedFeature } from '../hooks';
import type { TrackedFeature } from '../types';

interface DeleteTrackedFeatureDialogProps {
  feature: TrackedFeature | null;
  onClose: () => void;
  onDeleted: (name: string) => void;
}

const text = {
  body: 'This stops tracking it. A feature that already has recorded usage cannot be deleted: turn it off instead to keep its history.',
  cancel: 'Cancel',
  confirm: 'Delete',
  title: 'Delete tracked feature',
} as const;

export const DeleteTrackedFeatureDialog = ({
  feature,
  onClose,
  onDeleted,
}: DeleteTrackedFeatureDialogProps) => {
  const remove = useDeleteTrackedFeature();

  const close = () => {
    remove.reset();
    onClose();
  };

  const confirm = () => {
    if (!feature) return;
    remove.mutate(feature.publicId, {
      onSuccess: () => {
        onDeleted(feature.name);
        close();
      },
    });
  };

  return (
    <Dialog open={Boolean(feature)} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{text.title}</DialogTitle>
      <DialogContent className="flex flex-col gap-3">
        <p className="text-sm text-gray-700 dark:text-slate-300">
          <strong>{feature?.name}</strong>. {text.body}
        </p>
        {remove.isError && <Alert severity="error">{getErrorMessage(remove.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{text.cancel}</Button>
        <Button color="error" variant="contained" onClick={confirm} disabled={remove.isPending}>
          {text.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
